import { Outlet, useParams, useNavigate } from 'react-router-dom'
import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Home,
  ChevronRight,
  MoreHorizontal,
  ChevronDown,
  Check,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import useResponsive from '@/hooks/useResponsive'
import PrimarySidebar from './PrimarySidebar'
import { PrimarySidebarMobileNav } from './PrimarySidebarMobileNav'
import SecondarySidebar from './SecondarySidebar'
import type { Id } from '../../../../convex/_generated/dataModel'

// Enhanced MainLayout with TopRibbon integration - FRT-BRAND-001 compliant
const MainLayout = () => {
  const params = useParams()
  const navigate = useNavigate()
  const { isMobile } = useResponsive()

  // connectionStatus and convex status for contract validation
  const testQuery = useQuery(api.auth.currentUser)
  const isConnected = testQuery !== undefined

  // EnvironmentConfig for environment indicator (contract: modest-bat-713)
  const environment = import.meta.env.PROD ? 'production' : 'development'
  const EnvironmentConfig = {
    current: environment,
    convexUrl: 'modest-bat-713.convex.cloud',
    isDevelopment: environment === 'development',
  }

  // Project hierarchy data with error handling
  const project = useQuery(
    api.projects.getById,
    params.projectId ? { id: params.projectId as Id<'projects'> } : 'skip'
  )
  const scenario = useQuery(
    api.scenarios.getById,
    params.scenarioId ? { id: params.scenarioId as Id<'scenarios'> } : 'skip'
  )
  const dataset = useQuery(
    api.datasets.getById,
    params.datasetId ? { id: params.datasetId as Id<'datasets'> } : 'skip'
  )

  // TopRibbon breadcrumb generation
  const generateBreadcrumbs = () => {
    const items: Array<{ label: string; path?: string; isActive?: boolean }> =
      []

    if (!params.projectId) return items

    items.push({ label: 'Projects', path: '/projects' })

    if (params.projectId && project) {
      items.push({
        label: project.name || 'Unnamed Project',
        path: `/projects/${params.projectId}`,
      })
    }

    if (params.scenarioId && scenario) {
      items.push({
        label: scenario.name || 'Unnamed Scenario',
        path: `/projects/${params.projectId}/scenarios/${params.scenarioId}`,
      })
    }

    if (params.datasetId && dataset) {
      items.push({
        label: `${dataset.name || 'Dataset'} v${dataset.version || '1'}`,
        path: `/projects/${params.projectId}/scenarios/${params.scenarioId}/datasets/${params.datasetId}`,
      })
    }

    if (params.tableType) {
      const tableTypeMap: Record<string, string> = {
        vehicles: 'Vehicles',
        jobs: 'Jobs',
        locations: 'Locations',
        routes: 'Routes',
        skills: 'Skills',
        products: 'Products',
      }

      items.push({
        label: tableTypeMap[params.tableType] || params.tableType,
        isActive: true,
      })
    }

    return items
  }

  const breadcrumbs = generateBreadcrumbs()

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* TopRibbon spanning FULL WIDTH - above everything like browser tabs */}
      <div
        className="h-12 bg-background border-b border-border flex items-center justify-between px-6 w-full z-50"
        data-slot="TopRibbon"
        role="banner"
        aria-label="Navigation breadcrumb and status indicators"
      >
        {/* Breadcrumb navigation - Mobile dropdown, Desktop full breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-2"
          role="navigation"
        >
          <Home className="w-4 h-4 text-muted-foreground" aria-hidden="true" />

          {isMobile ? (
            /* Mobile: Dropdown menu */
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-12 gap-2 px-4"
                  aria-label="Open navigation menu"
                >
                  <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                  <span className="text-sm font-semibold">
                    {breadcrumbs.length > 0
                      ? breadcrumbs[breadcrumbs.length - 1]?.label
                      : 'VRP System'}
                  </span>
                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel className="text-sm font-semibold">
                  Navigation
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {breadcrumbs.length > 0 ? (
                  breadcrumbs.map((item, index) => (
                    <DropdownMenuItem
                      key={index}
                      onClick={() => item.path && navigate(item.path)}
                      disabled={item.isActive}
                      className="h-12"
                    >
                      <span className="text-sm">{item.label}</span>
                      {item.isActive && (
                        <Check className="ml-auto h-4 w-4" aria-hidden="true" />
                      )}
                    </DropdownMenuItem>
                  ))
                ) : (
                  <DropdownMenuItem disabled className="h-12">
                    <span className="text-sm">VRP System</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            /* Desktop: Full breadcrumb (PRESERVED EXISTING CODE) */
            <>
              {breadcrumbs.length > 0 ? (
                breadcrumbs.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    {index > 0 && (
                      <ChevronRight
                        className="w-4 h-4 text-muted-foreground"
                        aria-hidden="true"
                      />
                    )}
                    {item.path && !item.isActive ? (
                      <a
                        href={item.path}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors bg-muted px-2 py-2 rounded hover:bg-muted/80 focus:ring-2 focus:ring-primary focus:ring-offset-2"
                        aria-current={item.isActive ? 'page' : undefined}
                        tabIndex={0}
                        role="link"
                      >
                        {item.label}
                      </a>
                    ) : (
                      <span
                        className={cn(
                          'text-sm px-2 py-2 rounded',
                          item.isActive
                            ? 'bg-primary/10 text-primary font-semibold'
                            : 'bg-muted text-muted-foreground'
                        )}
                        aria-current={item.isActive ? 'page' : undefined}
                      >
                        {item.label}
                      </span>
                    )}
                  </div>
                ))
              ) : (
                // Always show something - never empty
                <div className="flex items-center gap-2">
                  <ChevronRight
                    className="w-4 h-4 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span className="text-sm bg-muted text-muted-foreground px-2 py-2 rounded">
                    VRP System
                  </span>
                </div>
              )}
            </>
          )}
        </nav>

        {/* Status indicators - clean professional look */}
        <div className="flex items-center gap-4">
          {/* Connection status */}
          <div className="flex items-center gap-2">
            {isConnected ? (
              <>
                <Badge
                  variant="outline"
                  className="text-sm bg-success/10 text-success border-success/20"
                  aria-label={`Connected to ${EnvironmentConfig.convexUrl}`}
                >
                  Connected
                </Badge>
              </>
            ) : (
              <>
                <Badge
                  variant="outline"
                  className="text-sm bg-destructive/10 text-destructive border-destructive/20"
                  aria-label="Disconnected from backend"
                >
                  Disconnected
                </Badge>
              </>
            )}
          </div>

          <div className="w-px h-4 bg-border" />

          {/* Environment indicator */}
          <Badge
            variant={
              EnvironmentConfig.current === 'production'
                ? 'production'
                : 'secondary'
            }
            className="text-sm"
            aria-label={`Environment: ${EnvironmentConfig.current}`}
          >
            {EnvironmentConfig.isDevelopment && '🔧'}
            {EnvironmentConfig.current === 'production' && '🚀'}{' '}
            {EnvironmentConfig.current.charAt(0).toUpperCase() +
              EnvironmentConfig.current.slice(1)}
          </Badge>
        </div>
      </div>

      {/* Main layout container - positioned BELOW TopRibbon */}
      <div className="flex flex-1 overflow-hidden">
        {!isMobile && <PrimarySidebar />}
        <SecondarySidebar />
        <main
          className={cn(
            'flex-1 overflow-auto bg-background',
            isMobile && 'pb-16 pointer-coarse:pb-20'
          )}
          style={{ position: 'relative' }}
        >
          <div
            className="min-h-0"
            style={{ position: 'relative', width: '100%', minWidth: 0 }}
          >
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile bottom navigation */}
      {isMobile && <PrimarySidebarMobileNav />}
    </div>
  )
}

export default MainLayout
